import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { AdminBundle } from 'src/api/optimalprint-sdk.d';
import CRUDTemplateForm from 'src/component/CRUDTemplate/CRUDTemplateForm';
import PageContent from 'src/component/PageContent';
import { getUrl, RouteName } from 'src/routing';
import {
  addProductDesignGenerationCandidate,
  fetchAvailableScenes,
  fetchProductDesignGenerationCandidateById,
  updateProductDesignGenerationCandidate,
} from 'src/service/AbstractProductDesignGenerationCandidate';
import { addForm, editForm } from './config';

const ProductDesignGenerationCandidatesEdit = () => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [availableScenes, setAvailableScenes] = useState<string[]>([]);
  const [title, setTitle] = useState('Add new abstract product design generation candidate');
  const emptyItem: AdminBundle.Entity.API.AbstractProductDesignGenerationCandidateControllerV1.AbstractProductDesignGenerationCandidateAddV1Request = {
    abstractProductSlug: '',
    generationType: '',
    suggestedProductUid: '',
  };

  const [
    editedItem,
    setEditedItem,
  ] = useState<
    AdminBundle.Entity.API.AbstractProductDesignGenerationCandidateControllerV1.AbstractProductDesignGenerationCandidateAddV1Request
    | AdminBundle.Entity.API.AbstractProductDesignGenerationCandidateControllerV1.AbstractProductDesignGenerationCandidateEditV1Request>(emptyItem);

  const params = useParams<{ id: string | undefined }>();
  const { id: abstractProductGenerationCandidateId } = params;

  const loadAvailableScenes = async (productUid: string) => {
    if (!productUid) {
      setAvailableScenes([]);
      return;
    }
    try {
      const scenes = await fetchAvailableScenes(productUid);
      setAvailableScenes(scenes || []);
    } catch (e) {
      console.error('Failed to fetch available scenes:', e);
      setAvailableScenes([]);
    }
  };

  const fetchData = async (id?: string) => {
    setIsLoading(true);
    if (id) {
      setTitle('Edit new abstract product design generation candidate');
      const response = await fetchProductDesignGenerationCandidateById(
        +id,
      ) as AdminBundle.Entity.API.AbstractProductDesignGenerationCandidateControllerV1.IAbstractProductDesignGenerationCandidate;
      setEditedItem(response);
      // Load available scenes for this product
      if (response.suggestedProductUid) {
        await loadAvailableScenes(response.suggestedProductUid);
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData(abstractProductGenerationCandidateId);
  }, [abstractProductGenerationCandidateId]);

  const onBackClick = () => {
    history.push(getUrl(RouteName.abstractProductDesignGenerationCandidate));
  };

  // Create edit form config with available scenes
  const getFormConfig = () => {
    if (!abstractProductGenerationCandidateId) {
      return addForm;
    }
    // Clone the edit form and add suggestions to the scenes field
    return {
      ...editForm,
      fields: editForm.fields.map((field) => {
        if (field.name === 'scenes') {
          return { ...field, suggestions: availableScenes };
        }
        return field;
      }),
    };
  };

  return (
    <PageContent title={title} isLoading={isLoading}>
      <CRUDTemplateForm
        editedItem={editedItem}
        formConfig={getFormConfig()}
        onSubmit={async (values) => {
          setIsLoading(true);
          try {
            if (abstractProductGenerationCandidateId) {
              await updateProductDesignGenerationCandidate(values);
            } else {
              await addProductDesignGenerationCandidate(values);
            }
            onBackClick();
          } catch (e) {
            setEditedItem(values);
            setIsLoading(false);
          }
        }}
        onBackClick={onBackClick}
      />
    </PageContent>
  );
};

export default ProductDesignGenerationCandidatesEdit;
